import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	createOrUpdatePendingInvoiceComment,
} from "~/modules/billing/api/pendingInvoice/pendingInvoiceApiDispatchers.ts";
import type { InvoiceCommentData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";
import { nl2br } from "~/utils/stringUtils.tsx";

type FormData = {
	comment: string | null;
}

type Props = {
	invoiceComment: InvoiceCommentData;
	onCancel: () => void;
	onSuccess: (comment: string) => void;
	userCanManageInvoices: boolean;
};

const UpdateUnprocessedInvoiceCommentForm = ({
	invoiceComment,
	onCancel,
	onSuccess,
	userCanManageInvoices,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => {
		return yup.object({
			comment: yup.string().nullable().default(""),
		});
	}, []);
	const defaultValues = useMemo(() => {
		return { comment: invoiceComment.comment || "" };
	}, [invoiceComment.comment]);

	const {
		control,
		handleSubmit,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const comment = data.comment || "";
			await createOrUpdatePendingInvoiceComment(invoiceComment.invoiceId, data.comment || "");
			onSuccess(comment);
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [invoiceComment.invoiceId, onSuccess]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Kommentar {userCanManageInvoices ? "bearbeiten" :""}</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				{userCanManageInvoices && <Textarea name="comment"
													control={control}
													autoFocus={true}
													placeholder="Hier Kommentartext eingeben..."
													rows={10} />}
				{!userCanManageInvoices &&
					<div className="whitespace-pre-wrap break-words rounded-lg border border-gray-300 bg-gray-50 p-4 text-gray-700">{nl2br(invoiceComment.comment || "Kein Kommentar vorhanden.")}</div>}
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				{userCanManageInvoices && <SubmitButton busy={isBusy}
														disabled={!formIsSubmittable}>
					Kommentar aktualisieren
				</SubmitButton>}
				<Button theme="white"
						onClick={onCancel}>
					{userCanManageInvoices ? "Abbrechen" : "Schließen"}
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateUnprocessedInvoiceCommentForm;
